/*
 *  -----------------------------------------------------------------------
 *    <copyright file="study-functions-grid.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client/react';
import { orderBy } from '@progress/kendo-data-query';

import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { useTranslation, useBoolean, useGridCells } from 'hooks';
import { GET_NAMED_ITEMS_BY_TYPE } from 'queries/common';
import { NamedItemFields } from 'queries/generated/fragments.generated';
import { NamedItemType } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import EditFunctionDialog from 'views/components/admin/studies/function-dialogs/edit-function';
import ExpandableGrid from 'views/components/core/expandable/grid';
import { GridColumnMenuActiveFilter } from 'views/components/core/grid-filters';

const StudyFunctionsGrid = ({ study, canEdit }: { study: any; canEdit: boolean }) => {
    const { t } = useTranslation();
    const { MaxLengthCell, YesNoCell, DateCell } = useGridCells();
    const [currentFunction, setCurrentFunction] = useState(null);
    const [studyFunctionsData, setStudyFunctionsData] = useState([]);
    const showEditFunctionDialog = useBoolean(false);

    const studyFunctionRequest = useQuery(GET_NAMED_ITEMS_BY_TYPE, {
        variables: {
            type: NamedItemType.RESPONSIBLE_FUNCTION,
        },
    });

    useEffect(() => {
        if (studyFunctionRequest.data?.namedItemsByType) {
            const studyFunctions =
                studyFunctionRequest.data?.namedItemsByType
                    .filter((fun: any) => study.studyFunctionIds.includes(fun.id) || fun.studyId === study.id)
                    .map((fun: any) => {
                        const _organizationFunction = fun.studyId === Constants.ObjectId.Empty;
                        return {
                            ...fun,
                            _organizationFunction,
                            created: _organizationFunction ? '' : fun.created,
                            createdBy: _organizationFunction ? null : fun.createdBy,
                        };
                    }) || [];
            const newData = restructuredUpdatedCreatedBy(studyFunctions);
            setStudyFunctionsData(
                orderBy(newData, [
                    { field: 'active', dir: 'desc' },
                    { field: 'name', dir: 'asc' },
                ]),
            );
        }
    }, [studyFunctionRequest, study]);

    return (
        <>
            <EditFunctionDialog
                open={showEditFunctionDialog.value}
                study={study}
                onClose={() => {
                    showEditFunctionDialog.setFalse();
                }}
                currentFun={currentFunction}
            />
            <ExpandableGrid
                customKey="study-function-grid"
                id="study-function"
                items={studyFunctionsData}
                groupable={false}
                reorderable
                scrollable="scrollable"
                showPager
                resizable
                pageable={false}
                options={{
                    allowExcelExport: false,
                    allowExpandCollapseAll: false,
                    useFilterChips: false,
                }}
                rowActions={(fun: NamedItemFields) => [
                    {
                        label: 'admin:editFunction',
                        icon: ['fal', 'link'],
                        onClick: () => {
                            setCurrentFunction(fun);
                            showEditFunctionDialog.setTrue();
                        },
                        enabled: canEdit,
                    },
                ]}
                detail={null}
                columns={[
                    {
                        field: 'recordId',
                        id: 'study-function-id',
                        label: 'ID',
                        filter: 'numeric',
                        minWidth: 140,
                    },
                    {
                        label: t('active'),
                        field: 'active',
                        headerClass: 'border',
                        id: 'study-function-active',
                        minWidth: 130,
                        filterUi: GridColumnMenuActiveFilter,
                        cells: { data: YesNoCell },
                    },
                    {
                        label: t('admin:function'),
                        id: 'study-function-name',
                        headerClass: 'border',
                        minWidth: 180,
                        field: 'name',
                        cells: { data: MaxLengthCell(50) },
                    },
                    {
                        label: t('ra:createdBy'),
                        field: '_createdByString',
                        headerClass: 'border',
                        id: 'risk-createdby',
                        minWidth: 200,
                    },
                    {
                        label: t('ra:createdDate'),
                        field: 'created',
                        headerClass: 'border',
                        filter: 'date',
                        id: 'risk-created',
                        minWidth: 200,
                        cells: { data: DateCell },
                    },
                    {
                        label: t('ra:updatedBy'),
                        field: '_updatedByString',
                        headerClass: 'border',
                        id: 'risk-updatedby',
                        minWidth: 200,
                    },
                    {
                        label: t('ra:updatedDate'),
                        field: 'updated',
                        headerClass: 'border',
                        filter: 'date',
                        id: 'risk-updated',
                        minWidth: 200,
                        cells: { data: DateCell },
                    },
                ]}
            />
        </>
    );
};

export default StudyFunctionsGrid;
